import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Text,
  Alert,
  Stack,
  Accordion,
  DefaultPageSkeleton,
  Datagrid,
  Badge,
  Menu,
  Checkbox,
  TextArea,
  FormRow,
  Link
} from '@efast_public/fjd-component-library';
import {
  ManagedOnlineService,
  VerarbeitungsverzeichnisResponse,
  VVVersion,
  GovOsResult,
  UpdateVVVersionRequest
} from '../api/govos.types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GovosApiRoutes,
  govosClient,
  invalidateConfiguredOnlinediensteCache
} from '../api/govos-api';
import { useNotification } from '../shared/notification/useNotification';
import { TableMenuButton } from '../shared/components/TableMenuButton';
import { urlToGovosVV } from '../utils/urlUtil';
import { VVExpertModeRedirectModal } from './VVExpertModeRedirectModal';
import { LocalStorageKeys } from '../utils/odUtil';

interface EditVerarbeitungsverzModalProps {
  onlinedienst: ManagedOnlineService;
  onClose: () => void;
  isOpen: boolean;
}

const PAGE_SIZE = 3;
const ROW_HEIGHT = 62;
const HEADER_HEIGHT = 56;

export function EditVerarbeitungsverzModal({
  onlinedienst,
  onClose,
  isOpen
}: EditVerarbeitungsverzModalProps) {
  const { t } = useTranslation();

  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();

  const [isAlternativePrivacyEnabled, setIsAlternativePrivacyEnabled] =
    useState(false);
  const [alternativePrivacyHtml, setAlternativePrivacyHtml] = useState('');
  const [redirectUrlToExportMode, setRedirectUrlToExportMode] = useState<
    string | null
  >(null);

  const { data, isLoading } = useQuery<VerarbeitungsverzeichnisResponse>({
    queryKey: [
      GovosApiRoutes.verarbeitungsverzeichnis(
        onlinedienst.startAppId,
        onlinedienst.processId
      )
    ]
  });

  useEffect(() => {
    if (data) {
      setIsAlternativePrivacyEnabled(data.alternativePrivacyEnabled);
      setAlternativePrivacyHtml(data.alternativePrivacyHtml);
    }
  }, [data]);

  const { mutate: createVerarbeitungsverz, isPending: isCreating } =
    useMutation({
      mutationFn: async () =>
        notifyUserFromRequest(
          () =>
            govosClient.post<GovOsResult<unknown>>(
              GovosApiRoutes.verarbeitungsverzeichnis(
                onlinedienst.startAppId,
                onlinedienst.processId
              )
            ),
          {
            successMessage: t('EditVerarbeitungsverzModal.successMessage'),
            errorMessage: t('EditVerarbeitungsverzModal.errorMessage'),
            loadingMessage: t('EditVerarbeitungsverzModal.loadingMessage')
          }
        ),
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: [
            GovosApiRoutes.verarbeitungsverzeichnis(
              onlinedienst.startAppId,
              onlinedienst.processId
            )
          ]
        });
        void queryClient.invalidateQueries({
          predicate: invalidateConfiguredOnlinediensteCache(
            onlinedienst.processId ?? ''
          )
        });
      }
    });

  const { mutate: updateVVVersion, isPending: isUpdating } = useMutation({
    mutationFn: async (payload: UpdateVVVersionRequest) =>
      notifyUserFromRequest(
        () =>
          govosClient.put<GovOsResult<unknown>>(
            GovosApiRoutes.verarbeitungsverzeichnis(
              onlinedienst.startAppId,
              onlinedienst.processId
            ),
            payload
          ),
        {
          successMessage: t('EditVerarbeitungsverzModal.update.successMessage'),
          errorMessage: t('EditVerarbeitungsverzModal.update.errorMessage'),
          loadingMessage: t('EditVerarbeitungsverzModal.update.loadingMessage')
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          GovosApiRoutes.verarbeitungsverzeichnis(
            onlinedienst.startAppId,
            onlinedienst.processId
          )
        ]
      });
      void queryClient.invalidateQueries({
        predicate: invalidateConfiguredOnlinediensteCache(
          onlinedienst.processId ?? ''
        )
      });
      onClose();
    }
  });

  const handleStartVerarbeitungsverz = () => {
    createVerarbeitungsverz();
  };

  const handleRedirectToExpertMode = (url: string) => {
    if (
      localStorage.getItem(
        LocalStorageKeys.shouldRedirectInstantlyToExpertMode
      ) === 'true'
    ) {
      window.location.href = url;
    } else {
      setRedirectUrlToExportMode(url);
    }
  };

  const handleSave = () => {
    const payload: UpdateVVVersionRequest = {
      alternativePrivacyEnabled: isAlternativePrivacyEnabled,
      alternativePrivacyHtml
    };
    updateVVVersion(payload);
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <Modal.Header id="modal-header">
          <Text size="xl" strong>
            {t('EditVerarbeitungsverzModal.title')}
          </Text>
        </Modal.Header>
        <Modal.Content>
          {isLoading && <DefaultPageSkeleton />}
          {!isLoading && data && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            >
              <Accordion label={t('EditVerarbeitungsverzModal.hint.title')}>
                <div style={{ paddingLeft: '24px' }}>
                  <ul
                    style={{
                      margin: '0',
                      paddingLeft: '20px',
                      fontSize: '14px',
                      lineHeight: '1.6'
                    }}
                  >
                    <li>{t('EditVerarbeitungsverzModal.hint.point1')}</li>
                    <li>{t('EditVerarbeitungsverzModal.hint.point2')}</li>
                    <li>{t('EditVerarbeitungsverzModal.hint.point3')}</li>
                    <li>{t('EditVerarbeitungsverzModal.hint.point4')}</li>
                    <li>{t('EditVerarbeitungsverzModal.hint.point5')}</li>
                  </ul>
                </div>
              </Accordion>

              {data.versions?.length === 0 && (
                <Alert
                  variant="warning"
                  message={
                    <Stack spacing="sm">
                      <Text>
                        {t('EditVerarbeitungsverzModal.warning.message')}
                      </Text>
                      <br />
                      <Button
                        variant="primary"
                        onClick={handleStartVerarbeitungsverz}
                        size="sm"
                        disabled={isCreating}
                      >
                        {t('EditVerarbeitungsverzModal.warning.action')}
                      </Button>
                    </Stack>
                  }
                />
              )}
              {data.versions?.length > 0 && (
                <div>
                  <div>
                    <Button
                      variant="primary-text"
                      onClick={handleStartVerarbeitungsverz}
                      size="sm"
                      disabled={isCreating}
                      beforeIcon="add-outline"
                    >
                      {t('EditVerarbeitungsverzModal.createNewVersion')}
                    </Button>
                  </div>
                  <Datagrid<VVVersion>
                    columns={[
                      {
                        id: 'vorlageVersion',
                        header: t(
                          'EditVerarbeitungsverzModal.table.vorlageVersion'
                        ),
                        accessorKey: 'vorlageVersion',
                        enableHiding: false,
                        size: 30,
                        minSize: 100,
                        enableSorting: false,
                        enableResizing: false,
                        sortingFn: 'auto'
                      },
                      {
                        id: 'datenVersion',
                        header: t(
                          'EditVerarbeitungsverzModal.table.datenVersion'
                        ),
                        accessorKey: 'datenVersion',
                        enableHiding: false,
                        size: 30,
                        minSize: 100,
                        enableSorting: true,
                        sortDescFirst: true,
                        enableResizing: false,
                        sortingFn: 'auto'
                      },
                      {
                        id: 'isActiveVersion',
                        header: t('EditVerarbeitungsverzModal.table.status'),
                        accessorKey: 'isActiveVersion',
                        enableHiding: false,
                        size: 30,
                        minSize: 200,
                        enableSorting: false,
                        enableResizing: false,
                        sortingFn: 'auto',
                        cell: (row) =>
                          row.getValue<boolean>() ? (
                            <Badge variant="success">Aktiv</Badge>
                          ) : (
                            <div>-</div>
                          )
                      },
                      {
                        id: 'datenschutzHinweis',
                        header: t(
                          'EditVerarbeitungsverzModal.table.datenschutzhinweise'
                        ),
                        accessorKey: 'approvedVersion',
                        enableHiding: false,
                        size: 30,
                        minSize: 200,
                        enableSorting: true,
                        enableResizing: true,
                        sortingFn: 'auto',
                        cell: (row) =>
                          row.getValue<number>() ? (
                            <Stack>
                              <Link
                                href={urlToGovosVV(
                                  onlinedienst.startAppId,
                                  onlinedienst.processId,
                                  row.row.original.vorlageVersion,
                                  row.row.original.datenVersion,
                                  'shap'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                {t(
                                  'EditVerarbeitungsverzModal.table.approvedVersion',
                                  {
                                    version: row.row.original.approvedVersion,
                                    date: row.row.original.approvedAt
                                  }
                                )}
                              </Link>
                            </Stack>
                          ) : (
                            <div>-</div>
                          )
                      },
                      {
                        id: 'verarbeitungsverzeichnis',
                        header: t(
                          'EditVerarbeitungsverzModal.table.verarbeitungsverzeichnis'
                        ),
                        accessorKey: 'name',
                        enableHiding: false,
                        size: 100,
                        minSize: 80,
                        enableSorting: false,
                        enableResizing: false,
                        sortingFn: 'auto',
                        cell: (row) => (
                          <TableMenuButton>
                            {row.row.original.isEditableVersion ? (
                              <Menu.MenuItem
                                key="edit"
                                icon="edit"
                                label={t(
                                  'EditVerarbeitungsverzModal.menu.bearbeiten'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const vv = row.row.original;
                                  handleRedirectToExpertMode(
                                    urlToGovosVV(
                                      onlinedienst.startAppId,
                                      onlinedienst.processId,
                                      vv.vorlageVersion,
                                      vv.datenVersion,
                                      'r'
                                    )
                                  );
                                }}
                              />
                            ) : (
                              <div />
                            )}
                            {row.row.original.isEditableVersion ? (
                              <Menu.MenuItem
                                key="preov"
                                icon="copy"
                                label={t(
                                  'EditVerarbeitungsverzModal.menu.vorbefuellen'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const vv = row.row.original;
                                  setRedirectUrlToExportMode(
                                    urlToGovosVV(
                                      onlinedienst.startAppId,
                                      onlinedienst.processId,
                                      vv.vorlageVersion,
                                      vv.datenVersion,
                                      'preov'
                                    )
                                  );
                                }}
                              />
                            ) : (
                              <div />
                            )}
                            <Menu.MenuItem
                              key="dl"
                              icon="view"
                              label={t(
                                'EditVerarbeitungsverzModal.menu.anzeigen'
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                const vv = row.row.original;
                                handleRedirectToExpertMode(
                                  urlToGovosVV(
                                    onlinedienst.startAppId,
                                    onlinedienst.processId,
                                    vv.vorlageVersion,
                                    vv.datenVersion,
                                    'dl'
                                  )
                                );
                              }}
                            />
                            {row.row.original.isEditableVersion &&
                            row.row.original.isValid ? (
                              <Menu.MenuItem
                                key="a"
                                icon="play"
                                label={t(
                                  'EditVerarbeitungsverzModal.menu.aktivieren'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const vv = row.row.original;
                                  handleRedirectToExpertMode(
                                    urlToGovosVV(
                                      onlinedienst.startAppId,
                                      onlinedienst.processId,
                                      vv.vorlageVersion,
                                      vv.datenVersion,
                                      'a'
                                    )
                                  );
                                }}
                              />
                            ) : (
                              <div />
                            )}
                          </TableMenuButton>
                        )
                      }
                    ]}
                    isDataLoading={isLoading}
                    data={data.versions ?? []}
                    labels={{
                      currentPage: t('AddOnlinedienstModal.labels.currentPage'),
                      detailButton: t(
                        'AddOnlinedienstModal.labels.detailButton'
                      ),
                      emptyData: t('AddOnlinedienstModal.labels.emptyData'),
                      nextPage: t('AddOnlinedienstModal.labels.nextPage'),
                      of: t('AddOnlinedienstModal.labels.of'),
                      page: t('AddOnlinedienstModal.labels.page'),
                      pageSize: t('AddOnlinedienstModal.labels.pageSize'),
                      previousPage: t(
                        'AddOnlinedienstModal.labels.previousPage'
                      ),
                      rows: t('AddOnlinedienstModal.labels.rows'),
                      tableTitle: '',
                      ascBtn: t('AddOnlinedienstModal.labels.ascBtn'),
                      descBtn: t('AddOnlinedienstModal.labels.descBtn'),
                      hideBtn: t('AddOnlinedienstModal.labels.hideBtn')
                    }}
                    manualPagination={false}
                    dataKey="id"
                    manualSorting={false}
                    areColumnsResizeable
                    tableHeight={PAGE_SIZE * ROW_HEIGHT + HEADER_HEIGHT}
                    paginationState={{ pageSize: PAGE_SIZE, pageIndex: 0 }}
                    multiSelect={false}
                  />
                </div>
              )}

              {data.versions?.length > 0 && (
                <div
                  style={{
                    marginTop: '24px',
                    padding: '16px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}
                >
                  <FormRow>
                    <Checkbox
                      checked={isAlternativePrivacyEnabled}
                      onChange={(e) =>
                        setIsAlternativePrivacyEnabled(e.currentTarget.checked)
                      }
                      label={t(
                        'EditVerarbeitungsverzModal.alternativePrivacy.checkbox'
                      )}
                    />
                  </FormRow>

                  <Text
                    size="s"
                    style={{
                      color: '#666',
                      marginTop: '8px',
                      marginBottom: '16px'
                    }}
                  >
                    {t(
                      'EditVerarbeitungsverzModal.alternativePrivacy.description'
                    )}
                  </Text>

                  {isAlternativePrivacyEnabled && (
                    <FormRow
                      label={t(
                        'EditVerarbeitungsverzModal.alternativePrivacy.label'
                      )}
                    >
                      <TextArea
                        value={alternativePrivacyHtml}
                        onChange={(e) =>
                          setAlternativePrivacyHtml(e.target.value)
                        }
                        rows={10}
                        placeholder={t(
                          'EditVerarbeitungsverzModal.alternativePrivacy.placeholder'
                        )}
                        style={{ width: '100%', fontFamily: 'monospace' }}
                      />
                    </FormRow>
                  )}
                  <div style={{ marginTop: '4px' }}>
                    <Button
                      variant="primary-text"
                      onClick={handleSave}
                      size="sm"
                      beforeIcon="save"
                      disabled={
                        isUpdating ||
                        (isAlternativePrivacyEnabled && !alternativePrivacyHtml)
                      }
                    >
                      {t('EditVerarbeitungsverzModal.alternativePrivacy.save')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Footer>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '12px',
              width: '100%'
            }}
          >
            <Button variant="primary" onClick={onClose} style={{ flex: 1 }}>
              {t('EditVerarbeitungsverzModal.actions.save')}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {redirectUrlToExportMode && (
        <VVExpertModeRedirectModal
          isOpen
          onClose={() => setRedirectUrlToExportMode(null)}
          onConfirm={() => {
            setRedirectUrlToExportMode(null);
            window.location.href = redirectUrlToExportMode;
          }}
        />
      )}
    </>
  );
}
